import { useState, useRef, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import styles from "./StreamChat.module.css";

export interface ChatMessage {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

export interface OutlineItem {
  id: string;
  content: string;
  note?: string;
  children?: OutlineItem[];
}

interface StreamChatProps {
  messages: ChatMessage[];
  onSendMessage: (content: string) => void;
  onEditMessage?: (index: number, content: string) => void;
  onDeleteMessage?: (index: number) => void;
  onTransitionToOutline?: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToOutline?: () => void;
  outlineItems?: OutlineItem[];
  isStreamSynced?: boolean;
  unsyncedStreamCount?: number;
  onImportStreamToOutline?: () => void;
  isLoading?: boolean;
  isTyping?: boolean;
  showOutlinePrompt?: boolean;
  isGeneratingOutline?: boolean;
  isZenMode?: boolean;
  aiConfigured?: boolean;
  aiEnabled?: boolean;
  wordCount?: number;
  lastSaved?: string;
}

export function StreamChat({
  messages,
  onSendMessage,
  onEditMessage,
  onDeleteMessage,
  onTransitionToOutline,
  onNavigateToSettings,
  onNavigateToOutline,
  outlineItems,
  isStreamSynced = true,
  unsyncedStreamCount = 0,
  onImportStreamToOutline,
  isLoading = false,
  isTyping = false,
  showOutlinePrompt = false,
  isGeneratingOutline = false,
  isZenMode = false,
  aiConfigured = true,
  aiEnabled: _aiEnabled = true,
  wordCount: _wordCount,
  lastSaved: _lastSaved,
}: StreamChatProps) {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const micCheckPendingRef = useRef(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const handleStartEdit = useCallback((index: number, content: string) => {
    setEditingIndex(index);
    setEditingContent(content);
  }, []);

  const handleConfirmEdit = useCallback(() => {
    if (editingIndex === null) return;
    const trimmed = editingContent.trim();
    if (trimmed && onEditMessage) {
      onEditMessage(editingIndex, trimmed);
    }
    setEditingIndex(null);
    setEditingContent("");
  }, [editingIndex, editingContent, onEditMessage]);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingContent("");
  }, []);

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        handleConfirmEdit();
      } else if (e.key === "Escape") {
        handleCancelEdit();
      }
    },
    [handleConfirmEdit, handleCancelEdit],
  );

  // Auto-focus the input textarea once it becomes available (after loading completes)
  useEffect(() => {
    if (!isLoading && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isLoading]);

  // Focus edit input when entering edit mode
  useEffect(() => {
    if (editingIndex !== null && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.style.height = "auto";
      editInputRef.current.style.height = `${editInputRef.current.scrollHeight}px`;
    }
  }, [editingIndex]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [inputValue]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = inputValue.trim();
    if (!trimmed || isTyping) return;

    onSendMessage(trimmed);
    setInputValue("");

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const startVoiceInput = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input is not supported in this browser");
      return;
    }

    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      return;
    }

    // Prevent overlapping mic permission checks — if a getUserMedia request
    // is already in flight, ignore subsequent clicks until it resolves.
    if (micCheckPendingRef.current) {
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    // Track finalized text to avoid duplication from interim results
    let finalizedText = "";
    // Capture the input value at the start of recording
    const baseText = inputValue;

    recognition.onstart = () => setIsRecording(true);
    recognition.onend = () => setIsRecording(false);

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let interimTranscript = "";

      // Process all results
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          // Only add newly finalized results (from resultIndex onwards)
          if (i >= event.resultIndex) {
            finalizedText += result[0].transcript;
          }
        } else {
          // Collect interim (not yet final) results
          interimTranscript += result[0].transcript;
        }
      }

      // Combine: base text + finalized speech + current interim preview
      const separator = baseText && !baseText.endsWith(" ") ? " " : "";
      setInputValue(baseText + separator + finalizedText + interimTranscript);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error("Speech recognition error:", event.error);
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        toast.error("Microphone access denied. Please allow microphone permissions and try again.");
      } else if (event.error !== "aborted") {
        toast.error("Voice input error. Please try again.");
      }
      setIsRecording(false);
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;

    // Pre-check microphone access before starting recognition
    // This prevents the button from briefly showing active state
    // when mic permissions are denied (onstart can fire before onerror)
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      micCheckPendingRef.current = true;
      navigator.mediaDevices
        .getUserMedia({ audio: true })
        .then((stream) => {
          // Stop the permission-check stream immediately
          stream.getTracks().forEach((track) => track.stop());
          // Guard against stale resolution — if the user clicked stop (or
          // another check started) while this promise was in flight, bail out.
          if (recognitionRef.current !== recognition) {
            return;
          }
          try {
            recognition.start();
          } catch (err) {
            console.error("Failed to start speech recognition:", err);
            toast.error("Could not start voice input. Check microphone permissions.");
            setIsRecording(false);
            recognitionRef.current = null;
          }
        })
        .catch(() => {
          toast.error(
            "Microphone access denied. Please allow microphone permissions and try again.",
          );
          recognitionRef.current = null;
        })
        .finally(() => {
          micCheckPendingRef.current = false;
        });
    } else {
      try {
        recognition.start();
      } catch (err) {
        console.error("Failed to start speech recognition:", err);
        toast.error("Could not start voice input. Check microphone permissions.");
        setIsRecording(false);
        recognitionRef.current = null;
      }
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  };

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner} />
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${isZenMode ? styles.zenMode : ""}`}>
      <div className={styles.messagesArea}>
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyHint}>{t("stream.emptyHint")}</p>
            {!aiConfigured && onNavigateToSettings && (
              <button
                type="button"
                className={styles.aiWarningButton}
                onClick={onNavigateToSettings}
                style={{ marginTop: "var(--spacing-2)" }}
              >
                {t("stream.configureAI", "Configure AI")}
              </button>
            )}
          </div>
        ) : (
          <div className={styles.messageList}>
            {messages.map((message, index) => (
              <div
                key={`${message.timestamp}-${index}`}
                className={`${styles.message} ${
                  message.role === "user"
                    ? styles.messageUser
                    : message.role === "assistant"
                      ? styles.messageAssistant
                      : styles.messageSystem
                }`}
              >
                {editingIndex === index ? (
                  <div className={styles.editWrapper}>
                    <textarea
                      ref={editInputRef}
                      className={styles.editInput}
                      value={editingContent}
                      onChange={(e) => {
                        setEditingContent(e.target.value);
                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                      onKeyDown={handleEditKeyDown}
                      rows={1}
                    />
                    <div className={styles.editActions}>
                      <button
                        type="button"
                        className={styles.editConfirm}
                        onClick={handleConfirmEdit}
                        aria-label={t("stream.saveEdit")}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        type="button"
                        className={styles.editCancel}
                        onClick={handleCancelEdit}
                        aria-label={t("stream.cancelEdit")}
                      >
                        <CloseIcon />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={styles.messageContent}>{message.content}</div>
                    {message.role === "user" && (onEditMessage || onDeleteMessage) && (
                      <div className={styles.messageActions}>
                        {onEditMessage && (
                          <button
                            type="button"
                            className={styles.messageActionButton}
                            onClick={() => handleStartEdit(index, message.content)}
                            aria-label={t("stream.editMessage")}
                          >
                            <PencilIcon />
                          </button>
                        )}
                        {onDeleteMessage && (
                          <button
                            type="button"
                            className={styles.messageActionButton}
                            onClick={() => onDeleteMessage(index)}
                            aria-label={t("stream.deleteMessage")}
                          >
                            <TrashIcon />
                          </button>
                        )}
                      </div>
                    )}
                  </>
                )}
                <span className={styles.messageTime}>{formatTime(message.timestamp)}</span>
              </div>
            ))}
            {isTyping && (
              <div className={`${styles.message} ${styles.messageAssistant}`}>
                <div className={styles.typingIndicator}>
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                  <span className={styles.typingDot} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
        {!isZenMode && outlineItems && outlineItems.length > 0 && (
          <div className={styles.outlineSection}>
            <div className={styles.outlineSectionHeader}>
              <OutlineIcon />
              <span className={styles.outlineSectionTitle}>
                {t("stream.outlineItems", "Outline")}
              </span>
              <span className={styles.outlineSectionCount}>
                {outlineItems.length}{" "}
                {outlineItems.length === 1 ? t("stream.item", "item") : t("stream.items", "items")}
                {!isStreamSynced && unsyncedStreamCount > 0 && (
                  <span className={styles.unsyncedBadge}>
                    +{unsyncedStreamCount} {t("stream.unsynced", "unsynced")}
                  </span>
                )}
              </span>
              {!isStreamSynced && unsyncedStreamCount > 0 && onImportStreamToOutline && (
                <button
                  type="button"
                  className={styles.syncStreamButton}
                  onClick={onImportStreamToOutline}
                >
                  {t("stream.syncStream", "Sync Stream")}
                </button>
              )}
              {onNavigateToOutline && (
                <button
                  type="button"
                  className={styles.outlineSectionLink}
                  onClick={onNavigateToOutline}
                >
                  {t("stream.viewOutline", "View Outline →")}
                </button>
              )}
            </div>
            <ul className={styles.outlineNodeList}>
              {outlineItems.map((item) => (
                <OutlineNodeItem key={item.id} item={item} depth={0} />
              ))}
            </ul>
          </div>
        )}
      </div>

      {!isZenMode && showOutlinePrompt && onTransitionToOutline && (
        <div className={styles.outlinePrompt}>
          <span className={styles.outlinePromptText}>
            <CheckCircleIcon className={styles.outlinePromptIcon} />
            {t("stream.outlinePrompt")}
          </span>
          <button
            type="button"
            className={`${styles.outlinePromptButton} ${isGeneratingOutline ? styles.outlinePromptButtonLoading : ""}`}
            onClick={onTransitionToOutline}
            disabled={isGeneratingOutline}
          >
            {isGeneratingOutline ? (
              <>
                <span className={styles.buttonSpinner} />
                {t("stream.creating")}
              </>
            ) : (
              t("stream.createOutline")
            )}
          </button>
        </div>
      )}

      {!aiConfigured && (
        <div className={styles.aiWarningBanner}>
          <span className={styles.aiWarningIcon}>⚠️</span>
          <span className={styles.aiWarningText}>
            {t(
              "stream.aiNotConfigured",
              "AI features are disabled. Configure an API key to get AI responses.",
            )}
          </span>
          {onNavigateToSettings && (
            <button type="button" className={styles.aiWarningButton} onClick={onNavigateToSettings}>
              {t("stream.configureAI", "Configure AI")}
            </button>
          )}
        </div>
      )}

      <form className={styles.inputArea} onSubmit={handleSubmit}>
        <div className={styles.inputWrapper}>
          <button
            type="button"
            className={`${styles.voiceButton} ${isRecording ? styles.voiceButtonActive : ""}`}
            onClick={startVoiceInput}
            aria-label={isRecording ? t("stream.stopRecording") : t("stream.startVoiceInput")}
          >
            <MicrophoneIcon />
          </button>
          <textarea
            ref={textareaRef}
            className={styles.input}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isRecording ? t("stream.listening") : t("stream.placeholder")}
            rows={1}
            disabled={isTyping}
          />
          <button
            type="submit"
            className={styles.sendButton}
            disabled={!inputValue.trim() || isTyping}
            aria-label={t("stream.sendMessage")}
          >
            <SendIcon />
          </button>
        </div>
      </form>
    </div>
  );
}

function OutlineNodeItem({ item, depth }: { item: OutlineItem; depth: number }) {
  return (
    <li className={styles.outlineNodeItem} style={{ paddingLeft: `${depth * 16}px` }}>
      <span className={styles.outlineNodeBullet}>•</span>
      <span className={styles.outlineNodeContent}>{item.content}</span>
      {item.note && <p className={styles.outlineNodeNote}>{item.note}</p>}
      {item.children && item.children.length > 0 && (
        <ul className={styles.outlineNodeList}>
          {item.children.map((child) => (
            <OutlineNodeItem key={child.id} item={child} depth={depth + 1} />
          ))}
        </ul>
      )}
    </li>
  );
}

function OutlineIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 4H14M2 8H10M2 12H14"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.3334 1.66669L9.16669 10.8334M18.3334 1.66669L12.5 18.3334L9.16669 10.8334M18.3334 1.66669L1.66669 7.50002L9.16669 10.8334"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function MicrophoneIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 1C10.9 1 10 1.9 10 3V12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12V3C14 1.9 13.1 1 12 1Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 10V12C19 15.866 15.866 19 12 19C8.13401 19 5 15.866 5 12V10"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 19V23"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 23H16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.08 1.92a1.5 1.5 0 0 1 2.12 0l.88.88a1.5 1.5 0 0 1 0 2.12L5.25 12.75 1 13l.25-4.25L10.08 1.92Z"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1.75 3.5H12.25M5.25 6.125V10.375M8.75 6.125V10.375M2.625 3.5L3.5 11.375C3.5 11.8582 3.89175 12.25 4.375 12.25H9.625C10.1082 12.25 10.5 11.8582 10.5 11.375L11.375 3.5M4.8125 3.5V2.1875C4.8125 1.70425 5.20425 1.3125 5.6875 1.3125H8.3125C8.79575 1.3125 9.1875 1.70425 9.1875 2.1875V3.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2.5 7.5L5.5 10.5L11.5 3.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18.3334 9.23335V10C18.3323 11.797 17.7504 13.5456 16.6745 14.9849C15.5985 16.4241 14.0861 17.4771 12.3629 17.9866C10.6396 18.4961 8.79774 18.4349 7.11208 17.8122C5.42642 17.1894 3.98723 16.0384 3.00915 14.5309C2.03108 13.0234 1.56651 11.2401 1.68475 9.44696C1.80299 7.6538 2.49769 5.94696 3.66525 4.58092C4.83281 3.21488 6.41068 2.26285 8.16351 1.86682C9.91635 1.47079 11.7502 1.65198 13.3917 2.38335"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.3334 3.33335L10 11.675L7.5 9.17502"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
